import { Dispatch } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { createJourney, getJourney, getListOfJourneysForParticularJourney, saveJourney } from '../../../../store/_api';
import { CreateJourneyData, CreateJourneyStepEdge, CreateJourneyStepNode, Service, ViewJourneyData } from '../../../../store/_api/TypeDefs';
import { FlowConnector, StepItem } from '../../../../_components/flow-builder/TypeDefs';
import { Actions, UpdateActions } from '../../reducer/TypeDefs';
import { EventMapDetails, FlowItemDataMap, LoaderHook, PayloadTransformation, StepJourneyDataMap, ToastMessageHook, TranslateHook } from '../TypeDefs';
import { createJourneyStepDetails, createJourneyStepEdges, createJourneyStepNodes } from './PrepareJourneyHelpers';
import { journeyBuilderRoutes } from '../../../../constants/JdRoutes';

/**
  * @method updateJourneyHandler
  * @description Handles the update journey API call.
  * @param {Object} options - The options object.
  * @param {(arg: boolean) => void} options.setEnableSetForApprovalBtn - The function to enable/disable the set for approval button.
  * @param {React.MutableRefObject<EventMapDetails | undefined>} options.stepDataRef - The mutable ref object for step data.
  * @param {React.MutableRefObject<{ nodes: JourneyStepNode[]; edges: JourneyStepEdge[]; } | undefined>} options.journeyDataRef - The mutable ref object for journey data.
  * @param {LoaderHook} options.loader - The loader hook for displaying loading state.
  * @param {ToastMessageHook} options.toastMessage - The toast message hook for displaying toast messages.
  * @param {TranslateHook} options.translate - The translate hook for translating text.
  * @param {Dispatch<UpdateActions>} options.dispatch - The dispatch function for updating the application state.
  * @param {boolean} options.designingStep - Flag indicating if the step is being designed.
  * @param {number} options.journeyVersion - The version of the journey.
  * @param {StepItem[]} options.stepItems - The array of step items.
  * @param {StepItem[]} options.deletedStepItems - The array of deleted step items.
  * @param {FlowConnector[]} options.stepConnectors - The array of step connectors.
  * @param {StepJourneyDataMap} options.stepJourneyDataMap - The map of step journey data.
  * @param {FlowItemDataMap} options.flowItemDataMap - The map of flow item data.
  * @param {PayloadTransformation[]} options.payloadTransformations - The array of payload transformations.
  * @param {string} options.journeyName - The name of the journey.
  * @param {string} options.journeyDescription - The description of the journey.
  */
export const updateJourneyHandler = (
  {
    navigate,
    cloneJourneyName,
    setEnableSetForApprovalBtn,
    stepDataRef,
    journeyDataRef,
    loader,
    toastMessage,
    translate,
    dispatch,
    designingStep,
    journeyVersion,
    stepItems,
    deletedStepItems,
    stepConnectors,
    deletedStepConnectors,
    stepJourneyDataMap,
    flowItemDataMap,
    payloadTransformations,
    journeyName,
    journeyDescription,
    journeyType,
    journeyCategory,
  }: {
    navigate?: NavigateFunction,
    cloneJourneyName:string,
    setEnableSetForApprovalBtn: (arg: boolean) => void,
    stepDataRef: React.MutableRefObject<EventMapDetails | undefined>,
    journeyDataRef: React.MutableRefObject<{
      nodes: CreateJourneyStepNode[];
      edges: CreateJourneyStepEdge[];
    } | undefined>,
    loader: LoaderHook,
    toastMessage: ToastMessageHook,
    translate: TranslateHook,
    dispatch: Dispatch<UpdateActions>,
    designingStep: boolean,
    journeyVersion: number,
    stepItems: StepItem[],
    deletedStepItems: StepItem[],
    stepConnectors: FlowConnector[],
    deletedStepConnectors: FlowConnector[],
    stepJourneyDataMap: StepJourneyDataMap,
    flowItemDataMap: FlowItemDataMap,
    payloadTransformations: PayloadTransformation[],
    journeyName: string,
    journeyDescription: string,
    journeyType: string,
    journeyCategory: string,
  }
) => {
  if (designingStep) {
    stepDataRef.current = undefined;
    dispatch({ type: Actions.STORE_STEP_JOURNEY });
    setEnableSetForApprovalBtn(false);
  } else {
    const allStepItems = [...stepItems, ...deletedStepItems];
    const allStepConnectors = [...stepConnectors, ...deletedStepConnectors];
    const { stepNodes } = createJourneyStepNodes({ stepItems: allStepItems, stepData: stepJourneyDataMap, stepEdges: allStepConnectors });
    const { stepEdges } = createJourneyStepEdges(stepConnectors);
    const stepDetails = createJourneyStepDetails({ cloneJourneyName, stepItems: allStepItems, stepData: stepJourneyDataMap, itemDataMap: flowItemDataMap, stepEdges: allStepConnectors, payloadTransformation: payloadTransformations });
    const journeyJsonDetails = {
      stepNodes,
      stepEdges
    };
    const completeJourney: CreateJourneyData = {
      journeyName,
      journeyDescription,
      journeyType,
      journeyCategory,
      journeyJsonDetails,
      stepDetails,
    };
    (async () => {
      loader.show();
      console.log('[SaveJourney] Full API payload:', JSON.stringify(completeJourney, null, 2));
      const resp = await saveJourney(completeJourney, journeyVersion);
      if (resp) {
        journeyDataRef.current = undefined;
        dispatch({
          type: Actions.SAVE_JOURNEY_SUCCESS,
          payload: {
            uuid: resp.uuid,
            steps: resp.stepDetails,
            status: resp.status,
            version: resp.version,
            comments: resp.journeyStatusHistory,
          }
        });
        loader.hide();
        setEnableSetForApprovalBtn(true);
        toastMessage.success(translate('journey_builder_component.title_toast_success'), translate('journey_builder_component.msg_journey_save_success'));
        if (navigate) {
          navigate({
            pathname: journeyBuilderRoutes.journeyCanvas.replace(':id', journeyName).replace(':version', resp.version.toString()).replace(':mode', 'edit'),
          }, {
            replace: true
          });
        }
      } else {
        loader.hide();
        setEnableSetForApprovalBtn(false);
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_journey_save_failed'));
      }
    })();
  }
};

/**
  * @method createJourneyHandler
  * @description Handles the create journey API call.
  * @param {Object} options - The options object.
  * @param {(arg: boolean) => void} options.setEnableSetForApprovalBtn - The function to enable/disable the set for approval button.
  * @param {React.MutableRefObject<EventMapDetails | undefined>} options.stepDataRef - The mutable ref object for step data.
  * @param {React.MutableRefObject<{ nodes: JourneyStepNode[]; edges: JourneyStepEdge[]; } | undefined>} options.journeyDataRef - The mutable ref object for journey data.
  * @param {LoaderHook} options.loader - The loader hook for displaying loading state.
  * @param {ToastMessageHook} options.toastMessage - The toast message hook for displaying toast messages.
  * @param {TranslateHook} options.translate - The translate hook for translating text.
  * @param {Dispatch<UpdateActions>} options.dispatch - The dispatch function for updating the application state.
  * @param {boolean} options.designingStep - Flag indicating if the step is being designed.
  * @param {StepItem[]} options.stepItems - The array of step items.
  * @param {StepItem[]} options.deletedStepItems - The array of deleted step items.
  * @param {FlowConnector[]} options.stepConnectors - The array of step connectors.
  * @param {StepJourneyDataMap} options.stepJourneyDataMap - The map of step journey data.
  * @param {FlowItemDataMap} options.flowItemDataMap - The map of flow item data.
  * @param {PayloadTransformation[]} options.payloadTransformations - The array of payload transformations.
  * @param {string} options.journeyName - The name of the journey.
  * @param {string} options.journeyDescription - The description of the journey.
  */
export const createJourneyHandler = (
  {
    navigate,
    cloneJourneyName,
    setEnableSetForApprovalBtn,
    stepDataRef,
    journeyDataRef,
    loader,
    toastMessage,
    translate,
    dispatch,
    designingStep,
    stepItems,
    deletedStepItems,
    stepConnectors,
    deletedStepConnectors,
    stepJourneyDataMap,
    flowItemDataMap,
    payloadTransformations,
    journeyName,
    journeyDescription,
    journeyType,
    journeyCategory,
  }: {
    navigate?: NavigateFunction,
    cloneJourneyName:string,
    setEnableSetForApprovalBtn: (arg: boolean) => void,
    stepDataRef: React.MutableRefObject<EventMapDetails | undefined>,
    journeyDataRef: React.MutableRefObject<{
      nodes: CreateJourneyStepNode[];
      edges: CreateJourneyStepEdge[];
    } | undefined>,
    loader: LoaderHook,
    toastMessage: ToastMessageHook,
    translate: TranslateHook,
    dispatch: Dispatch<UpdateActions>,
    designingStep: boolean,
    stepItems: StepItem[],
    deletedStepItems: StepItem[],
    stepConnectors: FlowConnector[],
    deletedStepConnectors: FlowConnector[],
    stepJourneyDataMap: StepJourneyDataMap,
    flowItemDataMap: FlowItemDataMap,
    payloadTransformations: PayloadTransformation[],
    journeyName: string,
    journeyDescription: string,
    journeyType: string,
    journeyCategory: string,
  }
) => {
  if (designingStep) {
    stepDataRef.current = undefined;
    dispatch({ type: Actions.STORE_STEP_JOURNEY });
    setEnableSetForApprovalBtn(false);
  } else {
    const allStepItems = [...stepItems, ...deletedStepItems];
    const allStepConnectors = [...stepConnectors, ...deletedStepConnectors];
    const { stepNodes } = createJourneyStepNodes({ stepItems: allStepItems, stepData: stepJourneyDataMap, stepEdges: allStepConnectors });
    const { stepEdges } = createJourneyStepEdges(stepConnectors);
    const stepDetails = createJourneyStepDetails({ cloneJourneyName, stepItems: allStepItems, stepData: stepJourneyDataMap, itemDataMap: flowItemDataMap, stepEdges: allStepConnectors, payloadTransformation: payloadTransformations });
    const journeyJsonDetails = {
      stepNodes,
      stepEdges
    };
    const completeJourney: CreateJourneyData = {
      journeyName,
      journeyDescription,
      journeyType,
      journeyCategory,
      journeyJsonDetails,
      stepDetails
    };

    (async () => {
      loader.show();
      console.log('[CreateJourney] Full API payload:', JSON.stringify(completeJourney, null, 2));
      const resp = await createJourney(completeJourney);
      if (resp) {
        journeyDataRef.current = undefined;
        dispatch({
          type: Actions.SAVE_JOURNEY_SUCCESS,
          payload: {
            uuid: resp.uuid,
            steps: resp.stepDetails,
            status: resp.status,
            version: resp.version,
            comments: resp.journeyStatusHistory,
          }
        });
        loader.hide();
        setEnableSetForApprovalBtn(true);
        toastMessage.success(translate('journey_builder_component.title_toast_success'), translate('journey_builder_component.msg_journey_save_success'));
        if (navigate) {
          navigate({
            pathname: journeyBuilderRoutes.journeyCanvas.replace(':id', journeyName).replace(':version', resp.version.toString()).replace(':mode', 'edit'),
          }, {
            replace: true
          });
        }
      } else {
        loader.hide();
        setEnableSetForApprovalBtn(false);
        toastMessage.failure(translate('journey_builder_component.title_toast_error'), translate('journey_builder_component.msg_journey_save_failed'));
      }
    })();
  }
};

/**
 * @method closeSaveJourneyModal
 * @description Closes the save journey modal.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 */
export const closeSaveJourneyModal = (dispatch: Dispatch<UpdateActions>) => {
  dispatch({ type: Actions.SHOW_SAVE_JOURNEY_MODAL });
};

/**
 * @method onEditHandler
 * @description Handles the onEdit action.
 * @param {(arg: boolean) => void} setShowModalForActiveEdit - The function to set the show modal state for active edit.
 * @param {LoaderHook} loader - The loader hook for displaying loading state.
 * @param {ToastMessageHook} toastMessage - The toast message hook for displaying toast messages.
 * @param {TranslateHook} translate - The translate hook for translating text.
 * @param {Dispatch<UpdateActions>} dispatch - The dispatch function for updating the application state.
 * @param {JourneyCanvasState} state - The current state of the journey canvas.
 * @param {string} options.journeyStatus - The status of the journey.
 * @param {string} options.journeyName - The name of the journey.
 */
export const onEditHandler = (
  {
    setShowModalForActiveEdit,
    loader,
    toastMessage,
    translate,
    dispatch,
    journeyStatus,
    journeyName
  }: {
    setShowModalForActiveEdit: (arg: boolean) => void,
    loader: LoaderHook,
    toastMessage: ToastMessageHook,
    translate: TranslateHook,
    dispatch: Dispatch<UpdateActions>,
    journeyStatus: string,
    journeyName: string
  }
) => {
  if (journeyStatus.toLocaleLowerCase() === 'active' || journeyStatus.toLocaleLowerCase() === 'inactive') {
    loader.show();
    (async () => {
      const resp = await getListOfJourneysForParticularJourney(journeyName);
      if (resp.isError) {
        loader.hide();
      } else {
        const value = resp.journeys.findIndex((item) => (['draft', 'returned', 'pending_approval'].includes(item.status.toLowerCase())));
        if (value === -1) {
          loader.hide();
          setShowModalForActiveEdit(true);
        } else {
          loader.hide();
          toastMessage.failure(
            translate('journey_builder_component.title_toast_error_for_active_edit'),
            translate('journey_builder_component.msg_if_editing_not_allowed')
          );
        }
      }
    })();
  } else {
    dispatch({
      type: Actions.VIEW_JOURNEY_INITIATED,
      payload: {
        flag: false,
      }
    });
  }
};

/**
 * @method fetchJourneyData
 * @description Fetches journey data based on the provided parameters and dispatches actions with the fetched data.
 * @param {Object} params - The parameters for fetching journey data.
 * @param {Promise[]} params.promises - An array of promises to which the promises returned by the getJourney calls will be added.
 * @param {Dispatch<UpdateActions>} params.dispatch - The dispatch function for updating the state with the fetched data.
 * @param {boolean} params.isCloneJourney - Indicates whether it is a clone journey.
 * @param {boolean} params.isCreate - Indicates whether it is a create operation.
 * @param {string} params.cloneJourneyName - The name of the journey to clone.
 * @param {number} params.cloneJourneyVersion - The version of the journey to clone.
 * @param {string} params.journeyId - The ID of the journey.
 * @param {number} params.versionForCurrentJourney - The version of the current journey.
 * @param {string} params.descriptionForCurrentJourney - The description of the current journey.
 * @param {string} params.groupForCurrentJourney - The group/category of the current journey.
 */
export const fetchJourneyData = (
  {
    promises,
    dispatch,
    isCloneJourney,
    isCreate,
    cloneJourneyName,
    cloneJourneyVersion,
    journeyId,
    versionForCurrentJourney,
    descriptionForCurrentJourney,
    groupForCurrentJourney
  } : {
    promises: Promise<Service[] | ViewJourneyData | undefined>[]
    dispatch: Dispatch<UpdateActions>,
    isCloneJourney: boolean,
    isCreate: boolean,
    cloneJourneyName: string,
    cloneJourneyVersion: number,
    journeyId: string,
    versionForCurrentJourney: number,
    descriptionForCurrentJourney: string,
    groupForCurrentJourney: string
  }
) => {
  if (isCloneJourney) {
    const journeyDataPromise = getJourney(cloneJourneyName, Number(cloneJourneyVersion)).then((resp) => {
      if (resp) {
        const filteredComments = resp.journeyStatusHistory?.filter((item) => item.comments !== '' && item.comments);
        dispatch({
          type: Actions.LOAD_JOURNEY_METADATA,
          payload: {
            name: journeyId,
            status: 'DRAFT',
            description: resp.journeyDescription, // descriptionForCurrentJourney,
            version: 1,
            type: resp.journeyType,
            category: resp.journeyCategory, // groupForCurrentJourney,
            uuid: resp.uuid,
            comments: filteredComments
          }
        });
      }
      return resp;
    });
    promises.push(journeyDataPromise);
  }

  if (!isCreate && !isCloneJourney) {
    const journeyDataPromise = getJourney(journeyId, versionForCurrentJourney).then((resp) => {
      if (resp) {
        const filteredComments = resp.journeyStatusHistory?.filter((item) => item.comments !== '' && item.comments);
        dispatch({
          type: Actions.LOAD_JOURNEY_METADATA,
          payload: {
            name: resp.journeyName,
            status: resp.status || 'DRAFT',
            description: resp.journeyDescription,
            version: resp.version || 1,
            type: resp.journeyType,
            category: resp.journeyCategory,
            uuid: resp.uuid,
            comments: filteredComments
          }
        });
      }
      return resp;
    });
    promises.push(journeyDataPromise);
  }
};
